import type { User } from "firebase/auth";
import { APEX_CUSTOM_TOKEN_URL } from "@/lib/apexCustomTokenEndpoint";
import { hasApexSessionObserved } from "@/lib/apexObservedState";

/**
 * KB-3271 — end the pro session when the apex (`kidsbooked.com`) session is gone.
 * The REVERSE half of federated logout: KB-3270 makes a pro logout clear the apex
 * cookie; this makes a director who signed out of `kidsbooked.com` stop being
 * signed into `pro.kidsbooked.com`.
 *
 * Pro cannot observe the apex cookie (`HttpOnly`, cross-origin) and there is no
 * push channel, so this is a POLL of `GET /api/auth/custom-token` and the logout
 * is eventually-consistent. That is acceptable: the threat it closes is the next
 * person picking up a shared front-desk device, not a same-instant revocation.
 *
 * ## ⛔ AC-2 (BLOCKING) — this is an ALLOWLIST, not a denylist. Fail OPEN.
 *
 * ONLY one endpoint answer may even be considered: a 2xx whose body is
 * `{ token: null, reason: 'unauthenticated' }` — a definitive "there is no apex
 * session." Since KB-3593 that answer is necessary, not sufficient: it says the
 * cookie is gone, not why, so it must also be confirmed by a revoked pro
 * credential (see `shouldEndProSessionForApex`). EVERYTHING else leaves the pro
 * session untouched:
 *
 *  - a network failure or the budget abort (fetch throws)  → `indeterminate`
 *  - a non-2xx status                                       → `indeterminate`
 *  - an unparseable body                                    → `indeterminate`
 *  - `reason: 'anonymous'` (AC-5 — an anon apex visitor is
 *     not a director, but is not evidence a director signed out)
 *  - `reason: 'verification-degraded'` (KB-2185 — the apex
 *     verifier was briefly unreachable and the cookie is STILL
 *     trusted; its own source calls it "emphatically NOT a
 *     logout signal") — NOT enumerated by the ticket's AC-2, found
 *     by reading the real endpoint; see the /boot premise-check note
 *  - any other `token: null` reason, a missing reason, a null body → `indeterminate`
 *  - a real token (`present`) — the director still has an apex session
 *
 * Signing live directors out of their own dashboard on a flaky network or a brief
 * apex outage is a far worse failure than a late logout — so the predicate is
 * expressed as a positive allowlist of the ONE definitive shape. A denylist
 * ("any token:null except anonymous ⇒ sign out") would eject a director during a
 * transient `verification-degraded` window, which is exactly the failure AC-2
 * forbids. `probeApexSession` therefore has NO branch that returns `absent`
 * except the explicit `unauthenticated` one, and it is red-proved in
 * `apexSessionChecker.test.ts`.
 */

/**
 * How long to wait on the apex presence poll before aborting. Mirrors the fetch
 * budgets in `apexSsoBootstrap.ts` / `apexSessionMint.ts`. The check is always
 * un-awaited by the caller (AC-3 — never blocks first render), so this bounds only
 * the leaked request.
 */
export const APEX_PRESENCE_FETCH_BUDGET_MS = 5000;

/**
 * AC-3 throttle: the minimum interval between focus/`visibilitychange` presence
 * checks, so rapid tab-switching cannot hammer the apex endpoint. One minute is
 * comfortably fine for an eventually-consistent shared-device logout.
 */
export const APEX_PRESENCE_CHECK_MIN_INTERVAL_MS = 60_000;

/** The one `reason` value that is a definitive "the apex session is gone." */
const DEFINITIVE_SIGNED_OUT_REASON = "unauthenticated";

/**
 * The three states the apex endpoint can leave us in.
 *  - `present`      — a live apex session (a real token came back).
 *  - `absent`       — a DEFINITIVE negative (`{ token: null, reason: 'unauthenticated' }`).
 *  - `indeterminate`— everything else; the pro session must be left untouched.
 */
export type ApexPresence = "present" | "absent" | "indeterminate";

/** The subset of the custom-token response body this checker reads. */
interface CustomTokenResponse {
  token?: unknown;
  reason?: unknown;
}

/**
 * Poll the apex custom-token endpoint and classify the result. NEVER throws —
 * every failure resolves to `indeterminate` (fail open). Returns `absent` ONLY on
 * the one definitive shape (AC-2).
 *
 * Not shared with `apexSsoBootstrap.ts`'s `fetchApexCustomToken`, which collapses
 * every negative to `null` and discards `reason`; the fail-open rule here turns
 * entirely on that `reason`, so it needs its own reason-aware read.
 */
export async function probeApexSession(): Promise<ApexPresence> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), APEX_PRESENCE_FETCH_BUDGET_MS);
  let res: Response;
  try {
    res = await fetch(APEX_CUSTOM_TOKEN_URL, {
      method: "GET",
      credentials: "include",
      signal: controller.signal,
    });
  } catch (fetchError) {
    // Network failure or the budget abort. Fail open — a flaky network is not a
    // logout signal.
    console.warn("Apex presence check: fetch failed:", fetchError);
    return "indeterminate";
  } finally {
    clearTimeout(timer);
  }

  // A non-2xx status is not a definitive "no session" (the endpoint answers 200
  // for the real signed-out case). Fail open, and do not even read the body.
  if (!res.ok) return "indeterminate";

  let body: CustomTokenResponse | null;
  try {
    body = (await res.json()) as CustomTokenResponse | null;
  } catch (parseError) {
    console.warn("Apex presence check: unparseable body:", parseError);
    return "indeterminate";
  }

  // A real token means the director still holds a live apex session.
  if (body && typeof body.token === "string" && body.token.length > 0) {
    return "present";
  }

  // From here the token is null/absent. Sign out ONLY on the explicit definitive
  // reason; the token-present case above has already returned, so the `reason`
  // check alone is the allowlist. `anonymous`, `verification-degraded`, a missing
  // reason and a null body all fall through to `indeterminate`.
  if (body && body.reason === DEFINITIVE_SIGNED_OUT_REASON) {
    return "absent";
  }

  return "indeterminate";
}

/**
 * KB-3593 — `absent` is NOT a sign-out on its own. Executed against staging
 * marketing: an expired apex cookie (the browser stops sending it), a malformed
 * one and an explicit sign-out all produce the byte-identical
 * `{ token: null, reason: 'unauthenticated' }`. A verifier blip does too, by
 * reading rather than execution: marketing's `transient-unresolved` leaves
 * `locals.user` null and `custom-token.ts` answers `unauthenticated`. Read alone,
 * the answer signed directors out of pro every time the 5-day apex cookie lapsed.
 *
 * The positive signal is the one an explicit kidsbooked.com sign-out leaves on
 * the uid itself: `POST /api/auth/logout` revokes every refresh token, and a
 * forced refresh then fails with an invalidation code. These are exactly the codes
 * the SDK's own `isUserInvalidated` treats as "this user is gone" (@firebase/auth
 * 1.13.3 inside firebase 12.16.0, `index-d90d2ee5.js:1353`) — on either one it has already signed the
 * user out by the time the refresh rejects.
 */
const INVALIDATED_CREDENTIAL_CODES = new Set(["auth/user-token-expired", "auth/user-disabled"]);

/**
 * The pro credential's state, read by forcing a token refresh.
 *  - `valid`         — the refresh succeeded; the uid was not signed out anywhere.
 *  - `revoked`       — the refresh was refused with an invalidation code.
 *  - `indeterminate` — anything else (network, App Check); leave the session alone.
 */
export type ProCredentialState = "valid" | "revoked" | "indeterminate";

export async function probeProCredential(user: Pick<User, "getIdToken">): Promise<ProCredentialState> {
  try {
    await user.getIdToken(true);
    return "valid";
  } catch (refreshError) {
    const code = (refreshError as { code?: unknown } | null)?.code;
    if (typeof code === "string" && INVALIDATED_CREDENTIAL_CODES.has(code)) return "revoked";
    console.warn("Apex presence check: credential refresh failed:", refreshError);
    return "indeterminate";
  }
}

/**
 * The full decision: should the pro session be ended because the director signed
 * out of kidsbooked.com?
 *
 *  - AC-1 gate: returns false immediately if no live apex session was ever observed
 *    for this uid — the checker does NOTHING unarmed, even on a definitive negative.
 *    This is what stops a silently-failed KB-3235 mint from ejecting a
 *    direct-login director.
 *  - AC-2: otherwise polls, and continues ONLY on the definitive `absent`.
 *  - KB-3593: `absent` is then confirmed against the pro credential. Only a
 *    `revoked` refresh ends the session; a `valid` one means the apex session
 *    merely lapsed, and the director stays signed in.
 */
export async function shouldEndProSessionForApex(user: Pick<User, "uid" | "getIdToken">): Promise<boolean> {
  if (!hasApexSessionObserved(user.uid)) return false;
  if ((await probeApexSession()) !== "absent") return false;
  return (await probeProCredential(user)) === "revoked";
}

/**
 * AC-3 throttle predicate, pure and separately testable. A focus/visibility check
 * runs only if it has never run (`lastRunAt === null`) or at least the minimum
 * interval has elapsed since the last one. Extracted from the wiring so the
 * throttle can be pinned in a `.ts` suite that runs locally, rather than only in
 * the `.tsx` AuthContext render suite that CI Root Tests validates.
 */
export function shouldRunPresenceCheck(
  lastRunAt: number | null,
  now: number,
  minIntervalMs: number = APEX_PRESENCE_CHECK_MIN_INTERVAL_MS,
): boolean {
  return lastRunAt === null || now - lastRunAt >= minIntervalMs;
}
